import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { AuthCallbackComponent } from './auth/auth-callback.component';
import { AuthGuard } from './auth/auth.guard';
import { LoginComponent } from './auth/login.component';

import { CompanyResolverComponent } from './components/company-resolver/company-resolver.component';
import { ComponentCredentialsFormComponent } from './components/component-credentials-form/component-credentials-form.component';
import { ComponentCredentialsListComponent } from './components/component-credentials-list/component-credentials-list.component';
import { DroneMaintenanceLogFormComponent } from './components/drone-maintenance-log/drone-maintenance-log-form/drone-maintenance-log-form.component';
import { LicenseBatchDetailsComponent } from './components/license-batch-details/license-batch-details.component';
import { LicenseBatchFormComponent } from './components/license-batch-form/license-batch-form.component';
import { LicenseBatchListComponent } from './components/license-batch-list/license-batch-list.component';
import { LicenseManageDevicesComponent } from './components/license-manage-devices/license-manage-devices.component';
import { LicenseManageUsersComponent } from './components/license-manage-users/license-manage-users.component';
import { LicenseAssignmentGuard } from './services/license-assignment.guard';
import { LicenseProductFormComponent } from './components/license-product-form/license-product-form.component';
import { LicenseProductListComponent } from './components/license-product-list/license-product-list.component';
import { ImpersonationComponent } from './components/impersonation/impersonation.component';
import { MembersTableComponent } from './components/members-table/members-table.component';
import { ProfileFormComponent } from './components/profile-form/profile-form.component';

import { FeatureFlagsEnum } from './feature-flag-constant';
import { AircraftRegistrationComponent } from './pages/aircraft-registration/aircraft-registration.component';
import { AllResourcesComponent } from './pages/all-resources/all-resources.component';
import { DeviceDetailsComponent } from './pages/device-details/device-details.component';
import { HelpComponent } from './pages/help/help.component';
import { JoinComponent } from './pages/join/join.component';
import { MyProfileComponent } from './pages/my-profile/my-profile.component';
import { ProgressComponent } from './pages/progress/progress.component';
import { ResourcesComponent } from './pages/resources/resources.component';
import { UploadsComponent } from './pages/uploads/uploads.component';

import { AuthLayoutComponent } from './components/auth-layout-component/auth-layout-component.component';
import { MainLayoutComponent } from './components/main-layout-component/main-layout-component.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { DeviceRegistrationComponent } from './pages/device-registration/device-registration.component';
import { MasterAdminCompaniesComponent } from './pages/master-admin-companies/master-admin-companies.component';
import { MasterAdminCompanyDetailsComponent } from './pages/master-admin-company-details/master-admin-company-details.component';
import { MasterAdminCompanyFormComponent } from './pages/master-admin-company-form/master-admin-company-form.component';
import { FeatureGuard } from './services/feature-guard';
import { ReportAccessGuard } from './services/report-access.guard';
import { aircraftRegistrationResolver } from './services/resolvers/aircraft-registration.resolver';
import { MaintenanceLogFormResolverService } from './services/resolvers/maintenance-log-form-resolver.service';
import { userCompanyResolver } from './services/resolvers/user-company.resolver';

const routes: Routes = [
  // ===== Auth-only layout (no sidebar) =====
  {
    path: 'login',
    component: AuthLayoutComponent,
    children: [
      { path: '', component: LoginComponent }, // /login
    ],
  },
  {
    path: 'auth',
    component: AuthLayoutComponent,
    children: [
      { path: ':provider', component: AuthCallbackComponent }, // /auth/:provider
    ],
  },

  // ===== Main app layout (with sidebar) =====
  {
    path: '',
    component: MainLayoutComponent,
    children: [
      { path: '', redirectTo: 'progress', pathMatch: 'full' },

      { path: 'join/:link', component: JoinComponent },

      {
        path: 'progress',
        canActivate: [AuthGuard],
        component: ProgressComponent,
      },
      {
        path: 'dashboard',
        canActivate: [AuthGuard, FeatureGuard, ReportAccessGuard],
        data: { featureFlag: FeatureFlagsEnum.DASHBOARD_PAGE },
        component: DashboardComponent,
      },
      {
        path: 'device-registration',
        canActivate: [AuthGuard],
        resolve: { companies: userCompanyResolver },
        component: DeviceRegistrationComponent,
      },
      {
        path: 'device-registration/:code',
        canActivate: [AuthGuard],
        resolve: { companies: userCompanyResolver },
        component: DeviceRegistrationComponent,
      },

      {
        path: 'resources',
        canActivate: [AuthGuard, FeatureGuard],
        data: { featureFlag: FeatureFlagsEnum.RESOURCES_PAGE },
        component: ResourcesComponent,
      },
      {
        path: 'uploads',
        canActivate: [AuthGuard, FeatureGuard],
        data: { featureFlag: FeatureFlagsEnum.UPLOAD_PAGE },
        component: UploadsComponent,
      },
      {
        path: 'licenses/batches',
        canActivate: [AuthGuard, FeatureGuard],
        data: { featureFlag: FeatureFlagsEnum.LICENSE_MANAGEMENT },
        children: [
          { path: '', component: LicenseBatchListComponent },
          {
            path: ':id/devices',
            canActivate: [LicenseAssignmentGuard],
            component: LicenseManageDevicesComponent,
          },
          {
            path: ':id/users',
            canActivate: [LicenseAssignmentGuard],
            component: LicenseManageUsersComponent,
          },
          {
            path: ':id',
            component: LicenseBatchDetailsComponent,
          },
        ],
      },
      {
        path: 'admin',
        canActivate: [AuthGuard, FeatureGuard],
        data: { featureFlag: FeatureFlagsEnum.ADMIN_PAGE },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'register-aircraft' },
          {
            path: 'register-aircraft',
            resolve: { companies: aircraftRegistrationResolver },
            component: AircraftRegistrationComponent,
          },
          {
            path: 'device-registration',
            canActivate: [AuthGuard],
            resolve: { companies: aircraftRegistrationResolver },
            component: DeviceRegistrationComponent,
          },
          {
            path: 'device-registration/:code',
            canActivate: [AuthGuard],
            resolve: { companies: aircraftRegistrationResolver },
            component: DeviceRegistrationComponent,
          },
          {
            path: 'all-resources',
            component: AllResourcesComponent,
          },
          {
            path: 'companies',
            component: MasterAdminCompaniesComponent,
          },
          {
            path: 'companies/create',
            component: MasterAdminCompanyFormComponent,
          },
          {
            path: 'companies/:id/edit',
            component: MasterAdminCompanyFormComponent,
          },
          {
            path: 'companies/:id',
            component: MasterAdminCompanyDetailsComponent,
          },
          {
            path: 'licenses',
            canActivate: [FeatureGuard],
            data: { featureFlag: FeatureFlagsEnum.LICENSE_MANAGEMENT },
            children: [
              {
                path: 'products',
                children: [
                  { path: '', component: LicenseProductListComponent },
                  { path: 'create', component: LicenseProductFormComponent },
                  { path: ':id/edit', component: LicenseProductFormComponent },
                ],
              },
              {
                path: 'component-credentials',
                children: [
                  { path: '', component: ComponentCredentialsListComponent },
                  {
                    path: 'create',
                    component: ComponentCredentialsFormComponent,
                  },
                  {
                    path: ':id/edit',
                    component: ComponentCredentialsFormComponent,
                  },
                ],
              },
              {
                path: 'batches',
                children: [
                  { path: '', component: LicenseBatchListComponent },
                  {
                    path: 'create',
                    resolve: { companies: aircraftRegistrationResolver },
                    component: LicenseBatchFormComponent,
                  },
                  {
                    path: ':id',
                    component: LicenseBatchDetailsComponent,
                  },
                  {
                    path: ':id/devices',
                    canActivate: [LicenseAssignmentGuard],
                    component: LicenseManageDevicesComponent,
                  },
                  {
                    path: ':id/users',
                    canActivate: [LicenseAssignmentGuard],
                    component: LicenseManageUsersComponent,
                  },
                  {
                    path: ':id/edit',
                    resolve: { companies: aircraftRegistrationResolver },
                    component: LicenseBatchFormComponent,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'maintenance-log',
        canActivate: [AuthGuard],
        children: [
          {
            path: 'create/:modelKey/:unitNumber',
            component: DroneMaintenanceLogFormComponent,
            resolve: { formData: MaintenanceLogFormResolverService },
          },
          {
            path: 'edit/:modelKey/:unitNumber/:logId/:configType',
            component: DroneMaintenanceLogFormComponent,
            resolve: { formData: MaintenanceLogFormResolverService },
          },
        ],
      },
      {
        path: 'device/:deviceId',
        canActivate: [AuthGuard, FeatureGuard],
        data: { featureFlag: FeatureFlagsEnum.RESOURCES_DETAILS },
        component: DeviceDetailsComponent,
      },
      {
        path: 'drones/:deviceId',
        redirectTo: 'device/:deviceId',
      },
      {
        path: 'profile',
        canActivate: [AuthGuard, FeatureGuard],
        data: { featureFlag: FeatureFlagsEnum.PROFILE_PAGE },
        component: MyProfileComponent,
        children: [
          { path: 'myprofile', component: ProfileFormComponent },
          { path: 'company', component: CompanyResolverComponent },
          { path: 'members', component: MembersTableComponent },
          { path: 'impersonation', component: ImpersonationComponent },
          { path: '**', redirectTo: 'myprofile' },
        ],
      },
      { path: 'help', canActivate: [AuthGuard], component: HelpComponent },
    ],
  },

  // Fallback
  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      // As we used APP_INITIALIZER for i18n/auth so we want first navigation to wait:
      // hence initialNavigation: 'enabledBlocking',
      initialNavigation: 'enabledBlocking',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
